import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    PlusIcon, PencilIcon, TrashIcon, PaperClipIcon, StarIcon, CheckBadgeIcon, MagnifyingGlassIcon, XMarkIcon,
    DocumentTextIcon, ChartBarIcon, ShoppingCartIcon, ChevronDownIcon, ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { inventoryAPI } from '../../../services/inventoryApi';
import { openFileInNewTab } from '../../../services/fileApi';
import { useAuth } from '../../../contexts/AuthContext';
import ConfirmationModal from '../../Common/ConfirmationModal';
import SearchablePagedDropdown from '../../Common/SearchablePagedDropdown';
import QuotationForm from './QuotationForm';
import QuotationComparison from './QuotationComparison';

const money = (n) => `Rs ${(Math.round((Number(n) || 0) * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const QUOTE_STATUS = {
    received: { label: 'Received', cls: 'bg-gray-100 text-gray-700' },
    selected: { label: 'Selected', cls: 'bg-green-100 text-green-800' },
    not_selected: { label: 'Not selected', cls: 'bg-gray-100 text-gray-500' },
    cancelled: { label: 'Cancelled', cls: 'bg-red-100 text-red-700' },
};
const STAGE = {
    no_quotes: { label: 'No quotes yet', cls: 'bg-amber-100 text-amber-800' },
    collecting: { label: 'Collecting quotes', cls: 'bg-blue-100 text-blue-800' },
    ready: { label: 'Selected — ready for PO', cls: 'bg-purple-100 text-purple-800' },
    po_raised: { label: 'PO raised', cls: 'bg-green-100 text-green-800' },
};
const TABS = [
    { key: '', label: 'All', count: 'all' },
    { key: 'no_quotes', label: 'No Quotes Yet', count: 'no_quotes' },
    { key: 'collecting', label: 'Collecting', count: 'collecting' },
    { key: 'ready', label: 'Ready for PO', count: 'ready' },
    { key: 'po_raised', label: 'PO Raised', count: 'po_raised' },
];

/**
 * Procurement → Supplier Quotations, grouped by purchase request: each approved request with its
 * quotations and stage (no quotes → collecting → selected → PO raised). Quotations are added, analysed,
 * selected and turned into the purchase order from here.
 */
const QuotationList = () => {
    const { hasPermission } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const prFilter = searchParams.get('pr') || '';
    const [groups, setGroups] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [stage, setStage] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ total: 0, pages: 1 });
    const [collapsed, setCollapsed] = useState({}); // prId → true when folded
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [picker, setPicker] = useState({ open: false, prId: '', label: '' });
    const [formState, setFormState] = useState({ open: false, pr: null, quotation: null, quotedSupplierIds: [] });
    const [compareFor, setCompareFor] = useState(null);

    const canCreate = hasPermission(['INVENTORY:QUOTATIONS:CREATE']);
    const canUpdate = hasPermission(['INVENTORY:QUOTATIONS:UPDATE']);
    const canDelete = hasPermission(['INVENTORY:QUOTATIONS:DELETE']);
    const canCreatePO = hasPermission(['INVENTORY:PURCHASE_ORDERS:CREATE']);

    useEffect(() => { const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400); return () => clearTimeout(t); }, [searchInput]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await inventoryAPI.getSupplierQuotationsByRequest({
                page, limit: 10, stage: stage || undefined, search: search || undefined, purchaseRequest: prFilter || undefined,
            });
            setGroups(res.data || []);
            setCounts(res.counts || {});
            setPagination(res.pagination || { total: 0, pages: 1 });
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to load supplier quotations');
        } finally { setLoading(false); }
    }, [page, stage, search, prFilter]);

    useEffect(() => { load(); }, [load]);

    const fetchApprovedRequests = async (term, pg, limit) => {
        const res = await inventoryAPI.getPurchaseRequests({ status: 'approved', search: term || undefined, page: pg, limit, sortBy: 'createdAt', sortOrder: 'desc' });
        const p = res.pagination || {};
        return { items: res.data || [], page: p.page || pg, pages: p.pages || 1, total: p.total || 0 };
    };

    // full purchase request (its lines) + the suppliers that already quoted it
    const openForm = async (prId, quotation = null) => {
        try {
            const [prRes, qRes] = await Promise.all([
                inventoryAPI.getPurchaseRequestById(prId),
                inventoryAPI.getSupplierQuotations({ purchaseRequest: prId, limit: 200 }),
            ]);
            const pr = prRes.data;
            if (pr?.status !== 'approved') {
                toast.error(`${pr?.prNumber || 'This purchase request'} is ${String(pr?.status || '').replace(/_/g, ' ')} — quotations can only be entered for approved requests`);
                return;
            }
            setFormState({ open: true, pr, quotation, quotedSupplierIds: (qRes.data || []).map(q => String(q.supplier?._id || q.supplier)) });
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to load the purchase request');
        }
    };

    const createPO = (prId) => navigate('/inventory/purchase-orders', { state: { selectedPRId: prId, fromPR: true, from: '/inventory/supplier-quotations' } });

    const handleDelete = async () => {
        try {
            const res = await inventoryAPI.deleteSupplierQuotation(deleteTarget._id);
            toast.success(res.message || 'Quotation deleted');
            setDeleteTarget(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete the quotation');
        }
    };

    const toggle = (id) => setCollapsed(c => ({ ...c, [id]: !c[id] }));

    return (
        <div className="bg-white shadow-sm border border-gray-200">
            {/* Header */}
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center">
                        <DocumentTextIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg font-semibold text-gray-900">Supplier Quotations</h1>
                        <p className="text-xs text-gray-500">Quotations for approved purchase requests, grouped by request — {pagination.total} request{pagination.total === 1 ? '' : 's'}</p>
                    </div>
                </div>
                {canCreate && (
                    <button onClick={() => setPicker({ open: true, prId: '', label: '' })}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-md hover:from-indigo-700 hover:to-purple-700">
                        <PlusIcon className="w-4 h-4 mr-1" /> Add Quotation
                    </button>
                )}
            </div>

            {/* Stage tabs + search */}
            <div className="px-4 sm:px-6 pt-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-b border-gray-200">
                <div className="flex gap-0 overflow-x-auto" role="tablist">
                    {TABS.map(t => (
                        <button key={t.key || 'all'} role="tab" aria-selected={stage === t.key} onClick={() => { setStage(t.key); setPage(1); }}
                            className={`px-3 py-2 text-xs font-medium border-b-2 whitespace-nowrap ${stage === t.key ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                            {t.label}
                            {counts[t.count] != null && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px]">{counts[t.count]}</span>}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2 pb-2">
                    {prFilter && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-indigo-50 text-indigo-700 rounded border border-indigo-100">
                            {groups[0]?.prNumber ? `PR: ${groups[0].prNumber}` : 'One purchase request'}
                            <button title="Show all" onClick={() => setSearchParams({})}><XMarkIcon className="w-3.5 h-3.5" /></button>
                        </span>
                    )}
                    <div className="relative">
                        <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-2 top-2" />
                        <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search PR, quotation, supplier, ref…"
                            className="pl-7 pr-2 py-1.5 text-sm border border-gray-300 rounded-md w-72 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" />
                    </div>
                </div>
            </div>

            {/* Groups */}
            <div className="p-4 sm:px-6 space-y-3" data-testid="quotation-groups">
                {loading ? (
                    <p className="py-10 text-center text-gray-400 text-sm">Loading…</p>
                ) : groups.length === 0 ? (
                    <p className="py-10 text-center text-gray-500 text-sm">No purchase requests found.</p>
                ) : groups.map(g => {
                    const st = STAGE[g.stage] || STAGE.collecting;
                    const open = g.status === 'approved';
                    const folded = !!collapsed[g._id];
                    return (
                        <div key={g._id} className="border border-gray-200 rounded-lg overflow-hidden" data-testid="pr-group" data-pr={g.prNumber}>
                            {/* Request row */}
                            <div className="flex flex-col lg:flex-row lg:items-center gap-2 px-3 py-2.5 bg-gray-50">
                                <button onClick={() => toggle(g._id)} className="flex items-center gap-2 text-left flex-1 min-w-0" aria-expanded={!folded}>
                                    {folded ? <ChevronRightIcon className="w-4 h-4 text-gray-500 flex-shrink-0" /> : <ChevronDownIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />}
                                    <span className="font-semibold text-indigo-700">{g.prNumber}</span>
                                    <span className="text-xs text-gray-500 truncate">
                                        {[g.costCenterLabel, (g.procurementType === 'FIXED_ASSET' ? 'Fixed asset' : g.procurementType === 'SERVICE' ? 'Service' : 'Inventory'), `${g.lineCount} line${g.lineCount === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}
                                    </span>
                                    <span className={`ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${st.cls}`} data-testid="pr-stage">
                                        {st.label}{g.stage === 'po_raised' && g.purchaseOrder?.orderNumber ? ` (${g.purchaseOrder.orderNumber})` : ''}
                                    </span>
                                </button>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                                    <span>{g.quotations.length} quotation{g.quotations.length === 1 ? '' : 's'}</span>
                                    {g.lowest && g.quotations.length > 1 && (
                                        <span>Lowest: <b className="text-gray-800">{g.lowest.supplier?.name}</b> {money(g.lowest.grandTotal)}</span>
                                    )}
                                    {g.selected && (
                                        <span className="text-green-700">Selected: <b>{g.selected.supplier?.name}</b> {money(g.selected.grandTotal)}</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    {open && canCreate && (
                                        <button onClick={() => openForm(g._id)} className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-indigo-700 bg-white border border-indigo-200 rounded hover:bg-indigo-50">
                                            <PlusIcon className="w-3.5 h-3.5 mr-1" /> Add Quotation
                                        </button>
                                    )}
                                    {g.quotations.length > 0 && (
                                        <button onClick={() => setCompareFor(g._id)} className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-indigo-700 bg-white border border-indigo-200 rounded hover:bg-indigo-50">
                                            <ChartBarIcon className="w-3.5 h-3.5 mr-1" /> Analyse
                                        </button>
                                    )}
                                    {g.stage === 'ready' && canCreatePO && (
                                        <button onClick={() => createPO(g._id)} className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-white bg-purple-600 rounded hover:bg-purple-700">
                                            <ShoppingCartIcon className="w-3.5 h-3.5 mr-1" /> Create PO
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Quotations of the request */}
                            {!folded && (g.quotations.length === 0 ? (
                                <p className="px-4 py-3 text-xs text-gray-500">No quotations yet — add the quotations received from suppliers.</p>
                            ) : (
                                <table className="min-w-full text-sm">
                                    <tbody className="divide-y divide-gray-100">
                                        {g.quotations.map(q => {
                                            const qs = QUOTE_STATUS[q.status] || QUOTE_STATUS.received;
                                            const editable = open && q.status !== 'selected';
                                            return (
                                                <tr key={q._id} className={q.status === 'selected' ? 'bg-green-50/60' : ''} data-testid="quotation-row">
                                                    <td className="pl-10 pr-3 py-2 whitespace-nowrap">
                                                        <div className="font-medium text-gray-900">{q.quotationNumber}</div>
                                                        {q.supplierReference && <div className="text-xs text-gray-500">Ref: {q.supplierReference}</div>}
                                                    </td>
                                                    <td className="px-3 py-2 text-gray-900">
                                                        {q.supplier?.name || '—'}
                                                        {q.attachments?.map((a, i) => (
                                                            <button key={a.fileId || i} type="button" onClick={() => openFileInNewTab(a.fileId).catch(() => toast.error('Could not open the document'))}
                                                                className="flex items-center text-xs text-indigo-600 hover:underline">
                                                                <PaperClipIcon className="w-3 h-3 mr-0.5" />{a.name}
                                                            </button>
                                                        ))}
                                                    </td>
                                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                                        <span className="font-semibold text-gray-900">{money(q.totals?.grandTotal)}</span>
                                                        {q.isLowestForRequest && g.quotations.length > 1 && (
                                                            <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800"><StarIcon className="w-3 h-3 mr-0.5" />Lowest</span>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{q.deliveryDays != null ? `${q.deliveryDays} days` : '—'}</td>
                                                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">Quoted {fmtDate(q.quotationDate)}{q.validUntil ? ` · valid to ${fmtDate(q.validUntil)}` : ''}</td>
                                                    <td className="px-3 py-2">
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${qs.cls}`}>
                                                            {q.status === 'selected' && <CheckBadgeIcon className="w-3.5 h-3.5 mr-0.5" />}{qs.label}
                                                        </span>
                                                        {q.status === 'selected' && q.selectionReason && <div className="text-xs text-gray-500 mt-0.5 max-w-[240px]">Reason: {q.selectionReason}</div>}
                                                    </td>
                                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                                        {canUpdate && editable && (
                                                            <button title="Edit quotation" onClick={() => openForm(g._id, q)} className="p-1 text-gray-400 hover:text-indigo-600"><PencilIcon className="w-4 h-4" /></button>
                                                        )}
                                                        {canDelete && editable && (
                                                            <button title="Delete quotation" onClick={() => setDeleteTarget(q)} className="p-1 text-gray-400 hover:text-red-600"><TrashIcon className="w-4 h-4" /></button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            ))}
                        </div>
                    );
                })}
            </div>

            {/* Pagination (per purchase request) */}
            <div className="px-4 sm:px-6 py-3 flex items-center justify-between border-t border-gray-200 text-xs text-gray-600">
                <span>Page {page} of {pagination.pages || 1}</span>
                <div className="flex gap-2">
                    <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40">Prev</button>
                    <button disabled={page >= (pagination.pages || 1)} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40">Next</button>
                </div>
            </div>

            {/* Header "Add Quotation": choose the purchase request */}
            {picker.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg">
                            <h3 className="text-base font-semibold text-white">Add Supplier Quotation</h3>
                            <button onClick={() => setPicker({ open: false, prId: '', label: '' })} className="text-white/80 hover:text-white"><XMarkIcon className="w-5 h-5" /></button>
                        </div>
                        <div className="p-4 space-y-2">
                            <label className="block text-xs font-medium text-gray-600">Approved Purchase Request <span className="text-red-500">*</span></label>
                            <SearchablePagedDropdown
                                value={picker.prId}
                                displayLabel={picker.label}
                                onChange={(id, label) => setPicker(p => ({ ...p, prId: id || '', label: label || '' }))}
                                fetchPage={fetchApprovedRequests}
                                getLabel={(pr) => pr.prNumber}
                                getSubLabel={(pr) => [pr.costCenterLabel, `${pr.lineItems?.length || 0} line(s)`, (pr.procurementType === 'FIXED_ASSET' ? 'Fixed asset' : pr.procurementType === 'SERVICE' ? 'Service' : 'Inventory')].filter(Boolean).join(' · ')}
                                noun="purchase request"
                                placeholder="Select approved purchase request…"
                                searchPlaceholder="Search by PR number…"
                                menuPlacement="down"
                            />
                            <p className="text-xs text-gray-500">Only approved requests without a purchase order can take quotations.</p>
                        </div>
                        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                            <button onClick={() => setPicker({ open: false, prId: '', label: '' })} className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                            <button disabled={!picker.prId} onClick={async () => { const id = picker.prId; setPicker({ open: false, prId: '', label: '' }); await openForm(id); }}
                                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50">Continue</button>
                        </div>
                    </div>
                </div>
            )}

            <QuotationForm
                isOpen={formState.open}
                onClose={() => setFormState({ open: false, pr: null, quotation: null, quotedSupplierIds: [] })}
                onSaved={load}
                purchaseRequest={formState.pr}
                quotation={formState.quotation}
                quotedSupplierIds={formState.quotedSupplierIds}
            />

            <QuotationComparison isOpen={!!compareFor} prId={compareFor} onClose={() => setCompareFor(null)} onChanged={load} />

            <ConfirmationModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Delete Quotation"
                message={deleteTarget ? `Delete quotation ${deleteTarget.quotationNumber} from ${deleteTarget.supplier?.name || 'this supplier'}?` : ''}
                confirmText="Delete"
                type="danger"
            />
        </div>
    );
};

export default QuotationList;
